// Product settings > Autopilot tab: the Product pages / Videos subtabs, the
// product's objectives, and what to do next — recommendations the brand
// acts on in their own platform (no button: the page is checked daily and
// the change is recorded when it shows up), applies (ours to do),
// dismisses, or adds to a strategy — plus the
// open opportunities the analysis found. Data comes from the modal's
// shared useProductAutopilot bundle so the Timeline tab reads the same rows.
import React, { useState } from 'react';
import { Box } from '@mui/material';
import classNames from 'classnames';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import axiosInstant from '../../../../api';
import {
  productAutopilotObjectivesEndpoint,
  productAutopilotRefreshEndpoint,
  recommendationAddToStrategyEndpoint,
  recommendationEndpoint,
} from '../../../../api/endpoints';
import { DotLottleLoader } from '../../../../components/Loader';
import { usePortalPath } from '../../../../hooks/usePortalPath';
import { apiErrorCode } from '../../../../utils/apiError';
import { IconChevronDown, IconRefresh } from '../../../../utils/icons';
import { AutopilotSides, TAB_PAGES, TAB_VIDEOS } from './AutopilotSides';
import {
  AddToStrategyResponse,
  AutopilotIntervention,
  AutopilotObjectiveMetric,
  AutopilotOpportunity,
  AutopilotPage,
  AutopilotRecommendation,
  AutopilotRefreshResult,
  BriefEvidence,
  ProductAutopilot,
} from '../../../../types/autopilot';
import {
  ACTION_LABELS,
  Pill,
  VerdictArrow,
  btnGhost,
  btnPrimary,
  btnSecondary,
  fmtMetricValue,
  fmtNum,
  humanize,
  metricLabel,
  opportunityKindLabel,
  predictionHeadline,
} from './autopilotLabels';
import { ProductAutopilotState } from './useProductAutopilot';

interface Props {
  desireid: number;
  // Same sourcing as the Product Pages tab (usePdpUrlBrand): the workspace
  // brand, or the operator's pick. Null lets the API fall back to the
  // product's own brand.
  brandId: number | null;
  state: ProductAutopilotState;
}

// One number beside an objective: the live value on the headline page,
// the page baseline in the tooltip. Returns shows the product's own
// estimate (a year, and a twelfth of it) instead of a rate.
function ObjectiveMetricValue({ m }: { m: AutopilotObjectiveMetric }) {
  const label = metricLabel(m.metric_key);
  if (m.metric_key === 'returns_rate' && m.detail) {
    const d = m.detail;
    return (
      <span title={`Estimated returns a year from the product's settings${d.annual_units ? ` (${fmtNum(d.annual_units)} units sold)` : ''}`}>
        Returns <span className="font-semibold text-text-1">{fmtNum(d.annual_returns)}</span>
        <span className="text-muted">/yr</span>{' '}
        <span className="font-semibold text-text-1">{fmtNum(d.monthly_returns, 1)}</span>
        <span className="text-muted">/mo</span>
        {m.value != null ? <span className="text-muted"> · {fmtMetricValue(m.metric_key, m.value)}</span> : null}
      </span>
    );
  }
  const title =
    m.value == null
      ? `${label}: no measurement yet`
      : `${label} now${m.availability === 'estimated' ? ' (estimate)' : ''}${m.baseline != null ? ` · ${fmtMetricValue(m.metric_key, m.baseline)} at the page baseline` : ''}`;
  return (
    <span title={title}>
      {label} <span className={classNames('font-semibold', m.value == null ? 'text-muted' : 'text-text-1')}>{fmtMetricValue(m.metric_key, m.value)}</span>
    </span>
  );
}

// The newest measured intervention for an objective — its verdict is the
// arrow next to that objective's numbers.
const latestVerdictFor = (interventions: AutopilotIntervention[], objectiveId: number) => {
  const hit = interventions.find((iv) => iv.objective && iv.objective.id === objectiveId && iv.summary && iv.summary.verdict);
  return hit && hit.summary ? hit.summary.verdict : null;
};

// Severity in words: the number means little to a brand user.
const severityWord = (severity: number) => {
  if (severity >= 4) return 'major';
  if (severity >= 3) return 'moderate';
  return 'minor';
};

// The analysis finding a recommendation was built from, when there is one.
const leadFinding = (rec: AutopilotRecommendation) => {
  const list = rec.config && Array.isArray(rec.config.findings) ? rec.config.findings : [];
  return list.length && list[0] && list[0].text ? list[0] : null;
};

// The card body: the rationale without the lead finding's text, which is
// already the title (the rules write "<text> <detail> Raised by …").
const bodyText = (rec: AutopilotRecommendation, lead: { text: string } | null) => {
  const r = (rec.rationale || '').trim();
  if (!lead) return r;
  const t = lead.text.trim();
  return r.startsWith(t) ? r.slice(t.length).trim() : r;
};

// An opportunity that only restates the recommendation it produced (same
// solve, or its sentence is already on the card) is no evidence to show.
const restatesRecommendation = (op: AutopilotOpportunity, rec: AutopilotRecommendation) => {
  if (op.kind === `solve:${rec.action_type}`) return true;
  const problem = (op.problem || '').trim();
  if (!problem) return false;
  if ((rec.rationale || '').indexOf(problem) >= 0) return true;
  const findings = rec.config && Array.isArray(rec.config.findings) ? rec.config.findings : [];
  return findings.some((f) => f && f.text && f.text.trim() === problem);
};

// One opportunity as an evidence line under a recommendation (or in the
// leftovers): what was seen, how serious, optionally where.
function EvidenceLine({ op, page }: { op: AutopilotOpportunity; page?: AutopilotPage | null }) {
  const sev = Number(op.severity) || 0;
  return (
    <li className="text-[12px] text-text-2 leading-[1.5]">
      <span className="font-semibold text-text-1">{opportunityKindLabel(op.kind)}</span>
      <span className="text-muted"> · {severityWord(sev)}</span>
      {page ? <span className="text-muted"> · {page.hostname}</span> : null}
      {op.problem ? <span> — {op.problem}</span> : null}
      {sev > 0 && Number(op.confidence) < 0.5 ? <span className="text-muted"> (not sure yet)</span> : null}
    </li>
  );
}

// A brief item's evidence as one line: citation texts (else their URLs),
// or the plain note it was given.
const evidenceText = (ev: string | BriefEvidence | BriefEvidence[] | null | undefined): string => {
  if (!ev) return '';
  if (typeof ev === 'string') return ev;
  const list = Array.isArray(ev) ? ev : [ev];
  return list
    .map((e) => (typeof e === 'string' ? e : (e && (e.text || e.url)) || ''))
    .filter(Boolean)
    .join(' · ');
};

function RecommendationCard({
  rec,
  pages,
  evidence,
  showPage,
  busy,
  onDismiss,
  onAddToStrategy,
}: {
  rec: AutopilotRecommendation;
  pages: AutopilotPage[];
  // The open opportunities this recommendation answers or that raise its priority.
  evidence: AutopilotOpportunity[];
  // Name the page on the card (multi-page products); evidence lines never do.
  showPage: boolean;
  busy: boolean;
  onDismiss: () => void;
  onAddToStrategy: () => void;
}) {
  const page = pages.find((p) => p.id === rec.desires_pdp_url_id);
  const hostname = page ? page.hostname : (rec.config && rec.config.hostname) || null;
  const [showWhy, setShowWhy] = useState(false);
  // A finding-driven recommendation is headed by what the analysis said to
  // do ("Add trust signals near the Add to cart button"); the solve type is
  // the category. Page-state ones (place this video, produce a video) keep
  // the action label as the title.
  const lead = leadFinding(rec);
  const title = lead ? lead.text : humanize(rec.action_type, ACTION_LABELS);
  const body = bodyText(rec, lead);
  const brief = rec.config && rec.config.brief;
  const items = (brief && brief.items) || [];
  const [showHow, setShowHow] = useState(false);
  // Who makes the change: the brand in their own platform (client_action),
  // the system itself (tdc_automatic: markup, transcript, chapters land in
  // the JSON-LD the site tag serves), or a strategy (production). Nothing
  // to click for the first two: the page is checked daily and detection
  // records the change and closes the card.
  const clientAction = rec.fulfillment === 'client_action' || rec.fulfillment === 'activation';
  const automatic = rec.fulfillment === 'tdc_automatic';
  const production = rec.fulfillment === 'production';
  const settled = rec.status !== 'proposed' && rec.status !== 'accepted';
  const instructions = rec.instructions || null;
  const working = busy ? 'Working…' : null;
  // The one primary button per fulfilment class; a client action has none.
  const primaryAction = () => {
    if (production) {
      return (
        <button type="button" className={btnPrimary} disabled={busy} onClick={onAddToStrategy} title="Open the strategy builder with this brief">
          {working || 'Add to strategy'}
        </button>
      );
    }
    return null;
  };
  return (
    <Box className="flex flex-col gap-2 rounded-[12px] border border-border-token bg-surface p-3">
      <Box className="flex items-start justify-between gap-2 flex-wrap">
        <Box className="flex flex-col gap-1 min-w-0">
          <Box className="flex items-center gap-2 flex-wrap">
            <span className="text-[13.5px] font-semibold text-text-1">{title}</span>
            {lead ? <Pill tone="neutral" title="The kind of change">{humanize(rec.action_type, ACTION_LABELS)}</Pill> : null}
            {rec.objective && rec.objective.name ? <Pill tone="accent">{rec.objective.name}</Pill> : null}
            {showPage && hostname ? <Pill tone="neutral" title="The product page this applies to">on {hostname}</Pill> : null}
          </Box>
          <Pill
            tone="neutral"
            title={
              rec.primary_prediction && rec.primary_prediction.availability === 'unavailable'
                ? `${metricLabel(rec.primary_prediction.metric_key)} has no measurement on this product yet, so there is no baseline to predict from`
                : 'What the model expects this change to do to the objective\'s main metric'
            }
          >
            {predictionHeadline(rec.primary_prediction)}
          </Pill>
        </Box>
        {clientAction && !settled ? (
          <Pill tone="neutral" title="Make the change in your platform whenever you like. We look at this page every day and record it, with the prediction above, the moment it shows up.">
            Checked daily
          </Pill>
        ) : null}
        {automatic && !settled ? (
          <Pill tone="neutral" title="We make this change ourselves in the markup your page carries. We look at the page every day and record it, with the prediction above, the moment it shows up.">
            Automatic · checked daily
          </Pill>
        ) : null}
      </Box>
      {body ? <p className="text-[12.5px] text-text-2 leading-[1.5] m-0">{body}</p> : null}
      {evidence.length ? (
        <Box>
          <button type="button" className={classNames(btnGhost, 'inline-flex items-center gap-1 no-underline')} onClick={() => setShowWhy((v) => !v)}>
            Why · {evidence.length} {evidence.length === 1 ? 'thing' : 'things'} we saw
            <IconChevronDown size={12} open={showWhy} />
          </button>
          {showWhy ? (
            <ul className="m-0 mt-1 pl-4">
              {evidence.map((op) => (
                <EvidenceLine key={op.id} op={op} />
              ))}
            </ul>
          ) : null}
        </Box>
      ) : null}
      {rec.file ? (
        <span className="text-[12px] text-text-2">
          Video: <span className="font-semibold text-text-1">{rec.file.name || `#${rec.file.id}`}</span>
        </span>
      ) : null}
      {rec.action_type === 'produce_video' && items.length ? (
        <Box className="flex flex-col gap-1">
          {brief && brief.title ? <span className="text-[12px] font-semibold text-text-1">{brief.title}</span> : null}
          <ul className="m-0 pl-4 text-[12px] text-text-2 leading-[1.5]">
            {items.slice(0, 5).map((it) => (
              <li key={it.key || it.title}>
                {it.title}
                {evidenceText(it.evidence) ? <span className="text-muted"> — {evidenceText(it.evidence)}</span> : null}
              </li>
            ))}
            {items.length > 5 ? <li className="text-muted list-none">+{items.length - 5} more</li> : null}
          </ul>
        </Box>
      ) : null}
      {rec.competing_change_intervention_id ? (
        <p className="text-[12px] text-accent-text m-0" title="Detection saw a video appear on this page that is not the expert video; this recommendation stays open">
          A video appeared on this page, but not this one.
        </p>
      ) : null}
      {clientAction && !settled && instructions ? (
        <Box className="rounded-[10px] border border-dashed border-border-token bg-surface-2 px-3 py-2">
          <button type="button" className={classNames(btnGhost, 'inline-flex items-center gap-1 no-underline')} onClick={() => setShowHow((v) => !v)}>
            {instructions.title}
            <IconChevronDown size={12} open={showHow} />
          </button>
          {showHow ? (
            <ol className="m-0 mt-1 pl-4 text-[12px] text-text-2 leading-[1.6]">
              {instructions.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          ) : null}
        </Box>
      ) : null}
      {!settled ? (
        <Box className="flex items-center gap-2 flex-wrap pt-1">
          {primaryAction()}
          <button type="button" className={btnGhost} disabled={busy} onClick={onDismiss}>
            Dismiss
          </button>
        </Box>
      ) : null}
    </Box>
  );
}

export function ProductAutopilotPanel({ desireid, brandId, state }: Props) {
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const { data, loading, error, reload } = state;
  const [busyId, setBusyId] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showOpportunities, setShowOpportunities] = useState(false);
  // Which side is showing: the objectives and recommendations below follow it.
  const [side, setSide] = useState(TAB_PAGES);
  // Optimistic objective toggles: the id being saved, reverted on error.
  const [objectiveOverrides, setObjectiveOverrides] = useState<Record<number, boolean>>({});
  const setObjective = async (objectiveId: number, enabled: boolean) => {
    setObjectiveOverrides((prev) => ({ ...prev, [objectiveId]: enabled }));
    try {
      await axiosInstant.patch(productAutopilotObjectivesEndpoint(desireid, brandId), { objective_id: objectiveId, enabled });
      await reload(true);
    } catch (e: any) {
      const code = apiErrorCode(e);
      if (code === 'AlwaysOnObjective') toast.error('This objective is always on.');
      else toast.error('Couldn\'t update the objective. Please try again.');
    } finally {
      setObjectiveOverrides((prev) => {
        const next = { ...prev };
        delete next[objectiveId];
        return next;
      });
    }
  };

  const dismiss = async (rec: AutopilotRecommendation) => {
    setBusyId(rec.id);
    try {
      await axiosInstant.patch(recommendationEndpoint(rec.id), { status: 'rejected' });
      await reload(true);
    } catch {
      toast.error('Couldn\'t dismiss this recommendation. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const addToStrategy = async (rec: AutopilotRecommendation) => {
    setBusyId(rec.id);
    try {
      const { data: body } = await axiosInstant.post<AddToStrategyResponse>(recommendationAddToStrategyEndpoint(rec.id));
      const prefill = body && body.prefill;
      let query = `recommendation_ids=${rec.id}&desireid=${desireid}`;
      if (prefill && prefill.pdp_url) query += `&pdp_url_id=${prefill.pdp_url.id}`;
      if (prefill && prefill.product && prefill.product.title) query += `&title=${encodeURIComponent(prefill.product.title)}`;
      navigate(p(`/opportunities/strategies/new?${query}`));
    } catch (e: any) {
      if (apiErrorCode(e) === 'NotAProductionRecommendation') toast.error('Only production recommendations go into a strategy.');
      else toast.error('Couldn\'t open the strategy builder. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: body } = await axiosInstant.post<AutopilotRefreshResult>(productAutopilotRefreshEndpoint(desireid, brandId));
      const n = body ? Number(body.recommendations || 0) : 0;
      toast.success(n ? `Analysis rules re-run — ${n} recommendation${n === 1 ? '' : 's'}.` : 'Analysis rules re-run.');
      await reload(true);
    } catch {
      toast.error('Couldn\'t re-run the analysis rules. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <DotLottleLoader fill={false} />;

  if (error) {
    return (
      <Box className="flex flex-col items-start gap-2 pt-4">
        <p className="text-[13px] text-down m-0">{error}</p>
        <button type="button" className={btnSecondary} onClick={() => reload(false)}>
          Try again
        </button>
      </Box>
    );
  }

  const bundle: ProductAutopilot | null = data;
  if (!bundle || (!bundle.pages.length && !(bundle.videos || []).length)) {
    return (
      <Box className="pt-4">
        <Box className="rounded-[12px] border border-dashed border-border-token bg-surface-2 p-5 text-center">
          <p className="text-[13.5px] font-semibold text-text-1 m-0">Add a product page or video to start</p>
          <p className="text-[12.5px] text-muted mt-1 mb-0">
            Autopilot watches your product pages, videos, channels and placements, finds what is holding them back and
            recommends the next change.
          </p>
        </Box>
      </Box>
    );
  }

  const onVideos = side === TAB_VIDEOS;
  // Product pages side: every product-page objective (the API seeds them),
  // the two always-on ones first, the rest switched here. Retail media and
  // omnichannel belong to the video channels and wait for them.
  // Videos side: the same list without the always-on two — Optimize PDP
  // and Be recommended by AI assistants recommend structured data and
  // other page changes that do not apply to a video on another channel.
  const shownObjectives = onVideos
    ? bundle.objectives.filter((o) => !o.always_on)
    : bundle.objectives.filter((o) => o.surface !== 'video');
  // Videos side: only video production — nothing that changes the product page.
  const next = bundle.recommendations
    .filter((r) => r.status === 'proposed' || r.status === 'accepted')
    .filter((r) => !onVideos || r.fulfillment === 'production')
    .sort((a, b) => b.priority - a.priority);
  // Evidence under each recommendation: the opportunity it answers, plus
  // the open ones on the same page and objective that no recommendation
  // answers (they raise this one's priority). What is left over is listed
  // once at the bottom; on the Videos side only content gaps, never page
  // findings.
  const openOpps: AutopilotOpportunity[] = bundle.opportunities.filter((o) => o.status === 'open');
  const answeredIds = new Set(bundle.recommendations.map((r) => r.opportunity_id).filter((id) => id != null));
  const sameScope = (op: AutopilotOpportunity, rec: AutopilotRecommendation) =>
    op.desires_pdp_url_id === rec.desires_pdp_url_id
    && !!op.objective && !!rec.objective && op.objective.id === rec.objective.id;
  const linkedTo = (rec: AutopilotRecommendation) =>
    openOpps.filter((op) => op.id === rec.opportunity_id || (!answeredIds.has(op.id) && sameScope(op, rec)));
  // Shown under the card: only what adds to it, never a restatement.
  const evidenceFor = (rec: AutopilotRecommendation) => linkedTo(rec).filter((op) => !restatesRecommendation(op, rec));
  const attached = new Set(next.flatMap((rec) => linkedTo(rec).map((op) => op.id)));
  const leftovers = openOpps
    .filter((op) => !attached.has(op.id))
    .filter((op) => !onVideos || String(op.kind).startsWith('content_type_gap'));
  const multiPage = bundle.pages.length > 1;

  return (
    <div className="flex flex-col gap-6 pt-4">
      {/* Product pages / Videos subtabs */}
      <section>
        <AutopilotSides
          desireid={desireid}
          brandId={brandId || (bundle.brand ? bundle.brand.id : null)}
          bundle={bundle}
          reload={reload}
          tab={side}
          onTabChange={setSide}
        />
      </section>

      {/* Objectives */}
      <section>
        <Box className="flex items-center justify-between gap-2 mb-2">
          <h4 className="text-[14px] font-semibold text-text-1 m-0">Objectives</h4>
          <span className="text-[11px] text-muted">
            {onVideos ? 'Objectives a video can serve on its channels' : 'Switch on the ones this product works toward'}
          </span>
        </Box>
        <Box className="flex flex-col gap-2">
          {shownObjectives.map((o) => {
            const enabled = objectiveOverrides[o.id] != null ? objectiveOverrides[o.id] : o.enabled;
            const verdict = latestVerdictFor(bundle.interventions, o.id);
            const saving = objectiveOverrides[o.id] != null;
            return (
              <Box
                key={o.id}
                className={classNames(
                  'flex items-center justify-between gap-3 flex-wrap rounded-[10px] border px-3 py-2',
                  enabled ? 'border-border-token bg-surface' : 'border-dashed border-border-token bg-surface-2',
                )}
              >
                <Box className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className={classNames('text-[13px] font-semibold', enabled ? 'text-text-1' : 'text-muted')}>{o.name || o.slug}</span>
                  {o.always_on ? (
                    <Pill tone="accent">Always on</Pill>
                  ) : (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={enabled}
                      disabled={saving}
                      onClick={() => setObjective(o.id, !enabled)}
                      className={classNames(
                        'text-[11px] font-semibold border rounded-[6px] px-2 py-[2px] cursor-pointer disabled:opacity-40',
                        enabled ? 'text-up border-up/40 hover:bg-surface-2' : 'text-muted border-border-token hover:bg-surface',
                      )}
                    >
                      {enabled ? 'On' : 'Off'}
                    </button>
                  )}
                  {o.source === 'strategy' ? <span className="text-[11px] text-muted">from a strategy</span> : null}
                </Box>
                {enabled && o.metrics.length ? (
                  <Box className="flex items-center gap-3 text-[12px] text-text-2 flex-wrap">
                    {o.metrics.map((m) => (
                      <ObjectiveMetricValue key={m.metric_key} m={m} />
                    ))}
                    <VerdictArrow verdict={verdict} />
                  </Box>
                ) : null}
              </Box>
            );
          })}
          {!shownObjectives.length ? <p className="text-[12.5px] text-muted m-0">No objectives yet.</p> : null}
        </Box>
      </section>

      {/* Recommendations */}
      <section>
        <Box className="flex items-center justify-between gap-2 mb-2">
          <Box className="flex items-baseline gap-2 flex-wrap">
            <h4 className="text-[14px] font-semibold text-text-1 m-0">Recommendations</h4>
            <span className="text-[11px] text-muted">What to do next, and why</span>
          </Box>
          <button type="button" className={classNames(btnSecondary, 'inline-flex items-center gap-1')} disabled={refreshing} onClick={refresh} title="Look at the latest analysis and Consumer Voice results again">
            <IconRefresh size={12} />
            {refreshing ? 'Re-running…' : 'Re-run analysis rules'}
          </button>
        </Box>
        {next.length ? (
          <Box className="flex flex-col gap-2">
            {next.map((rec) => (
              <RecommendationCard
                key={rec.id}
                rec={rec}
                pages={bundle.pages}
                evidence={evidenceFor(rec)}
                showPage={multiPage}
                busy={busyId === rec.id}
                onDismiss={() => dismiss(rec)}
                onAddToStrategy={() => addToStrategy(rec)}
              />
            ))}
          </Box>
        ) : (
          <Box className="rounded-[12px] border border-dashed border-border-token bg-surface-2 p-4 text-center">
            <p className="text-[12.5px] text-muted m-0">
              {onVideos ? 'No video production to recommend right now.' : 'Nothing to do right now — the next analysis will look again.'}
            </p>
          </Box>
        )}
        {leftovers.length ? (
          <Box className="mt-3">
            <button
              type="button"
              className={classNames(btnGhost, 'inline-flex items-center gap-1')}
              onClick={() => setShowOpportunities((v) => !v)}
              title="Findings without a recommendation of their own yet"
            >
              {leftovers.length} other {leftovers.length === 1 ? 'thing' : 'things'} the analysis noticed
              <IconChevronDown size={12} open={showOpportunities} />
            </button>
            {showOpportunities ? (
              <ul className="m-0 mt-2 pl-4">
                {leftovers.map((op) => (
                  <EvidenceLine key={op.id} op={op} page={multiPage ? bundle.pages.find((pg) => pg.id === op.desires_pdp_url_id) : null} />
                ))}
              </ul>
            ) : null}
          </Box>
        ) : null}
      </section>
    </div>
  );
}
